
import { supabase } from './supabase';
import { Profile } from './auth.service';

export interface StaffProfile extends Profile {
    permissions: string[];
    is_master: boolean;
    email: string;
}

export const StaffService = {
    /**
     * Get all staff for the current user's company
     */
    async getStaff(companyId: string) {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data as StaffProfile[];
    },

    /**
     * Update staff permissions
     */
    async updatePermissions(userId: string, permissions: string[]) {
        const { error } = await supabase
            .from('profiles')
            .update({ permissions })
            .eq('id', userId);

        if (error) throw error;
        return true;
    },

    /**
     * Toggle a user as master (Admin only usually, but allowed by Master here if policy allows)
     */
    async toggleMaster(userId: string, isMaster: boolean) {
        const { error } = await supabase
            .from('profiles')
            .update({ is_master: isMaster })
            .eq('id', userId);

        if (error) throw error;
        return true;
    },

    /**
     * Remove a staff member from the company
     */
    async removeStaff(userId: string) {
        const { error } = await supabase
            .from('profiles')
            .delete()
            .eq('id', userId);

        if (error) throw error;
        return true;
    },

    /**
     * In this simple version, creating an account requires a service role or edge function.
     * We provide a helper to invite via code or explain the flow.
     */
    async getRegistrationUrl(companyCode: string) {
        const baseUrl = window.location.origin + window.location.pathname;
        // The registration page is /register, and it expects a company code
        return `${baseUrl}#/register?code=${companyCode}`;
    },

    /**
     * Helper to get a pre-formatted invite text
     */
    getInviteText(companyName: string, companyCode: string, registrationUrl: string) {
        return `【${companyName}】邀请您加入物流通 PDA 系统。\n\n1. 点击链接点击连接：${registrationUrl}\n2. 输入公司验证码：${companyCode}\n3. 完成注册后联系主管开通权限。`;
    },

    /**
     * Create a staff member directly using supabase.auth.signUp().
     * This safely uses the anon key (no secret key needed in browser).
     * The admin's current session is preserved and restored after creation.
     */
    async createStaffDirectly(params: { 
        email: string; 
        password?: string; 
        full_name: string; 
        role: string; 
        company_id: string;
    }) {
        // 1. Save the current admin session before creating a new user
        const { data: { session: adminSession } } = await supabase.auth.getSession();
        
        if (!adminSession) {
            throw new Error('当前未登录，请先登录管理员账号');
        }

        try {
            // 2. Create User via supabase.auth.signUp() - uses anon key, safe for browser
            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email: params.email,
                password: params.password || 'hedui888',
                options: {
                    data: {
                        full_name: params.full_name,
                        role: params.role,
                        company_id: params.company_id
                    },
                    // Skip email confirmation by not requiring it
                    // Note: Email confirmation behavior is controlled in Supabase Dashboard settings
                }
            });

            if (signUpError) {
                throw new Error(signUpError.message || '创建用户失败');
            }

            if (!signUpData.user) {
                throw new Error('创建用户失败：未返回用户信息');
            }

            const userId = signUpData.user.id;

            // 3. Restore the admin session immediately
            await supabase.auth.setSession({
                access_token: adminSession.access_token,
                refresh_token: adminSession.refresh_token,
            });

            // 4. Update the profile with company_id and role using admin's session
            //    The trigger handle_new_user should create the profile,
            //    but we ensure company_id is set correctly.
            //    Wait a moment for the trigger to complete
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    company_id: params.company_id,
                    role: params.role,
                    full_name: params.full_name
                })
                .eq('id', userId);

            if (profileError) {
                console.error('Failed to link profile:', profileError);
                // Don't throw - user is already created in Auth
            }

            return signUpData.user;
        } catch (error) {
            // Always restore admin session on error
            if (adminSession) {
                await supabase.auth.setSession({
                    access_token: adminSession.access_token,
                    refresh_token: adminSession.refresh_token,
                });
            }
            throw error;
        }
    }
};
